"use client"

import { Tooltip } from "@mui/material"
import { Close } from "@mui/icons-material"
import EmojiPicker from "emoji-picker-react"
import {
  ArrowDown,
  ChevronLeft,
  Circle,
  Edit,
  FileText,
  Mail,
  MessageSquareReply,
  MoreVertical,
  Paperclip,
  Phone,
  Search,
  Send,
  Smile,
  User,
  X,
  Menu,
} from "lucide-react"
import { Fragment, useState, useEffect, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { updateLiveUserDetailAction } from "../../../services/action/common"
import { handlePopupModalAction } from "../../../services/action/event"

const ChatArea = ({
  audioRef,
  showChat,
  isMobile,
  stats,
  activeFilter,
  handleFilterClick,
  searchQuery,
  setSearchQuery,
  parentRef,
  rowVirtualizer,
  allLiveUser,
  activeVisitor,
  unreadMessage,
  getChatListOfUser,
  formatMessageTime,
  visitors,
  truncateString,
  countryFlags,
  setShowChat,
  setopenLiveChat,
  showOptions,
  setShowOptions,
  chatContainerRef,
  handleChatScroll,
  messageRefs,
  highlightedMessage,
  messages,
  renderRepliedMessage,
  renderMessageContent,
  handleReply,
  showScrollButton,
  scrollToBottom,
  replyingTo,
  cancelReply,
  attachments,
  isImageUrl,
  removeAttachment,
  isUploading,
  message,
  setMessage,
  handleFileUpload,
  showEmojiPicker,
  setShowEmojiPicker,
  handleEmojiClick,
  handleSendMessageWithAttachments,
  // Filter props
  websites,
  currentWebsite,
  handleWebsiteChange,
  dateRange,
  handleDateRangeChange,
  clearDateFilter,
  // New modal filter props
  showFilterModal,
  toggleFilterModal,
  selectedCountry,
  handleCountryChange,
  resetAllFilters,
  renderPlatformIcon,
  showDetailedView,
  toggleDetailedView,
  setPageData,
  getAllLiveUser,
  filter,
}) => {
  // State for edit modal
  const { user } = useSelector((state) => state.authenticationReducer)
  const { openChatList } = useSelector((state) => state.commonReducer)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editFormData, setEditFormData] = useState({
    name: openChatList?.client?.name || "",
    email: openChatList?.client?.email || "",
    number: openChatList?.client?.phone || "",
  })
  const dispatch = useDispatch()
  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
    number: "",
  })

  // Add state for detailed view search and pagination
  const [detailedViewSearch, setDetailedViewSearch] = useState("")

  // Add a ref for the search timeout at the top of the component (after other state declarations)
  const searchTimeout = useRef(null)
  const mainSearchTimeout = useRef(null)

  // Update the search input handler to send the query to the backend
  const handleDetailedSearch = (e) => {
    const searchValue = e.target.value
    setDetailedViewSearch(searchValue)

    // Reset to page 1 when search changes
    setCurrentPage(1)

    // Use a timeout to debounce the API call
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }

    // Store the search value in a ref to prevent duplicate calls with the same value
    searchTimeout.current = setTimeout(() => {
      // Only call the API if getAllLiveUser is a function and the search value has changed
      if (typeof getAllLiveUser === "function") {
        // Set a flag to indicate we're searching to prevent fetchMoreData from being called
        window.isSearching = true

        // Call the API with the search query
        getAllLiveUser({
          page: 1,
          tempSearchQuery: searchValue,
          resetPageData: true,
          firstTime: true,
        })

        // Reset the flag after a delay
        setTimeout(() => {
          window.isSearching = false
        }, 500)
      }
    }, 500) // 500ms debounce
  }

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // Remove the client-side filtering logic and use the data directly from allLiveUser
  const paginatedVisitors = useMemo(() => {
    if (!allLiveUser?.data) return []

    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage

    // Use a slice of the data from the API
    return allLiveUser.data.slice(startIndex, endIndex)
  }, [allLiveUser?.data, currentPage, itemsPerPage])

  // Calculate total pages based on the total from the API
  const totalPages = useMemo(() => {
    if (!allLiveUser?.paginations?.total) return 1
    return Math.ceil(allLiveUser.paginations.total / itemsPerPage)
  }, [allLiveUser?.paginations?.total, itemsPerPage])

  // Handle page change to fetch data from the API
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return
    setCurrentPage(page)

    // Check if getAllLiveUser is a function before calling it
    if (typeof getAllLiveUser === "function") {
      // Call the API with the new page
      getAllLiveUser({
        page: page,
        tempSearchQuery: detailedViewSearch,
        resetPageData: false,
        firstTime: false,
      })
    }
  }

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear the error for this field when user types
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }))
  }

  // Add a validateForm function
  const validateForm = () => {
    let isValid = true
    const errors = {
      name: "",
      email: "",
      number: "",
    }

    // Name validation
    if (!editFormData.name.trim()) {
      errors.name = "Name is required"
      isValid = false
    }

    // Email validation
    if (!editFormData.email.trim()) {
      errors.email = "Email is required"
      isValid = false
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)) {
      errors.email = "Please enter a valid email address"
      isValid = false
    }

    // Phone number validation
    if (!editFormData.number.trim()) {
      errors.number = "Phone number is required"
      isValid = false
    } else if (!/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(editFormData.number)) {
      errors.number = "Please enter a valid phone number"
      isValid = false
    }

    setFormErrors(errors)
    return isValid
  }

  // Handle edit form submission
  const handleEditSubmit = (e) => {
    e.preventDefault()

    if (validateForm()) {
      // Here you would typically call an API to update the user data
      dispatch(
        updateLiveUserDetailAction({
          userType: user?.userType?.toLowerCase(),
          data: { ...editFormData, userId: openChatList?.client?._id },
        }),
      )
      // Close the modal after submission
      setShowEditModal(false)
      // You would typically update the user data in your state/store here
    }
  }

  // Open edit modal with current user data
  const openEditModal = () => {
    setEditFormData({
      name: openChatList?.client?.name || "",
      email: openChatList?.client?.email || "",
      number: openChatList?.client?.phone || "",
    })
    setFormErrors({
      name: "",
      email: "",
      number: "",
    })
    setShowEditModal(true)
    setShowOptions(false) // Close the options dropdown
  }

  // Update the applyFilters function to ensure filters aren't applied until the Apply button is clicked
  const applyFilters = () => {
    // Reset pagination and fetch new data with all current filters
    if (typeof setPageData === "function") {
      setPageData({ page: 1, limit: 17 })
    }

    if (typeof getAllLiveUser === "function") {
      getAllLiveUser({
        page: 1,
        tempWebsite: filter?.website,
        from: filter?.from,
        to: filter?.to,
        country: filter?.country,
        resetPageData: true,
        firstTime: true,
      })
    }

    // Close the modal
    toggleFilterModal(false)
  }

  // Filter visitors based on search query
  const filteredVisitors = useMemo(() => {
    if (!allLiveUser?.data) return []

    if (!detailedViewSearch.trim()) return allLiveUser.data

    const searchLower = detailedViewSearch.toLowerCase()
    return allLiveUser.data.filter(
      (visitor) =>
        (visitor.visitorId && visitor.visitorId.toLowerCase().includes(searchLower)) ||
        (visitor.name && visitor.name.toLowerCase().includes(searchLower)) ||
        (visitor.country && visitor.country.toLowerCase().includes(searchLower)) ||
        (visitor.deviceType && visitor.deviceType.toLowerCase().includes(searchLower)) ||
        (visitor.website && visitor.website.toLowerCase().includes(searchLower)) ||
        (visitor.websiteName && visitor.websiteName.toLowerCase().includes(searchLower)) ||
        (visitor.refralUrl && visitor.refralUrl.toLowerCase().includes(searchLower)) ||
        (visitor.status && visitor.status.toLowerCase().includes(searchLower)),
    )
  }, [allLiveUser?.data, detailedViewSearch])

  // Calculate total pages
  // const totalPages = Math.ceil(filteredVisitors.length / itemsPerPage)

  // Get current page data
  // const paginatedVisitors = useMemo(() => {
  //   const startIndex = (currentPage - 1) * itemsPerPage
  //   return filteredVisitors.slice(startIndex, startIndex + itemsPerPage)
  // }, [filteredVisitors, currentPage, itemsPerPage])

  // Handle page change
  // const handlePageChange = (page) => {
  //   if (page < 1 || page > totalPages) return
  //   setCurrentPage(page)
  // }

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [detailedViewSearch])

  const pageNumbers = useMemo(() => {
    const range = (start, end) => {
      const length = end - start + 1
      return Array.from({ length }, (_, idx) => idx + start)
    }

    const totalPageNumbers = 7 // adjust as needed
    const visiblePages = totalPageNumbers - 2 // exclude ellipsis

    if (totalPages <= totalPageNumbers) {
      return range(1, totalPages)
    }

    const leftSide = 3 // adjust as needed
    const rightSide = 3 // adjust as needed

    if (currentPage <= leftSide) {
      return [...range(1, visiblePages), "...", totalPages]
    }

    if (currentPage > totalPages - rightSide) {
      return [1, "...", ...range(totalPages - visiblePages + 1, totalPages)]
    }

    const middle = range(currentPage - 1, currentPage + 1)
    return [1, "...", ...middle, "...", totalPages]
  }, [currentPage, totalPages])

  const handleMainSearch = (e) => {
    const value = e.target.value
    setSearchQuery(value)

    if (mainSearchTimeout.current) {
      clearTimeout(mainSearchTimeout.current)
    }

    mainSearchTimeout.current = setTimeout(() => {
      if (typeof getAllLiveUser === "function") {
        // Set a flag to indicate we're searching
        window.isSearching = true

        getAllLiveUser({
          page: 1,
          tempSearchQuery: value,
          resetPageData: true,
          firstTime: true,
        })

        // Reset the flag after a delay
        setTimeout(() => {
          window.isSearching = false
        }, 500)
      }
    }, 500) // 500ms debounce
  }

  // Clean up timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current)
      if (mainSearchTimeout.current) clearTimeout(mainSearchTimeout.current)
    }
  }, [])

  return (
    <Fragment>
      <audio ref={audioRef} src="/assets/seen.wav" />
      <div className="flex flex-col h-[100vh] bg-gray-50 font-inter">
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Left Panel - Visitor List */}
          <div
            className={`${
              showChat && isMobile ? "hidden" : "flex"
            } md:flex md:w-80 w-full bg-white border-r border-gray-200 flex-col min-h-0 min-w-[280px]`}
          >
            {/* Quick Stats */}
            <div className="flex items-center justify-between p-2 bg-white border-b border-gray-200">
              <button
                onClick={() => dispatch(handlePopupModalAction({ isOpenSidebar: true }))}
                className="p-2 hover:text-white rounded-md hover:bg-[#0A2547] transition-colors lg:hidden"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-sm font-medium text-gray-700 lg:hidden">Visitor Statistics</h2>
            </div>

            <div className="grid grid-cols-2 gap-1.5 p-1.5 bg-gray-50 border-b border-gray-200">
              {stats.map((stat) => (
                <button
                  key={stat.label}
                  onClick={() => handleFilterClick(stat)}
                  className={`flex items-center p-2 rounded-lg transition-all ${
                    activeFilter === stat.label
                      ? "bg-[#00204A] text-white shadow-md"
                      : "bg-white hover:bg-gray-50 active:bg-gray-100"
                  }`}
                >
                  <div className="text-xs">
                    <div className={`font-medium ${activeFilter === stat.label ? "text-gray-100" : "text-gray-500"}`}>
                      {stat.label}
                    </div>
                    <div className={`font-bold ${activeFilter === stat.label ? "text-white" : "text-[#00204A]"}`}>
                      {stat.value}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Search and Filter */}
            <div className="p-1.5">
              <div className="flex items-center space-x-1.5">
                <div className="relative flex-1">
                  <input
                    type="text"
                    className="w-full pr-3 py-1.5 pl-8 text-sm bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00204A] focus:border-[#00204A]"
                    placeholder="Search visitors..."
                    value={searchQuery}
                    onChange={handleMainSearch}
                  />
                  <Search className="absolute left-2.5 top-1.5 h-4 w-4 text-gray-400" />
                </div>
                <button
                  onClick={toggleFilterModal}
                  className={`p-1.5 rounded-md text-sm font-medium flex items-center ${
                    currentWebsite !== "all website" || dateRange.from || dateRange.to || selectedCountry !== "all"
                      ? "bg-[#00204A] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  title="Open filters"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    />
                  </svg>
                  Filter
                  {(currentWebsite !== "all website" ||
                    dateRange.from ||
                    dateRange.to ||
                    selectedCountry !== "all") && (
                    <span className="ml-1 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full">
                      {(currentWebsite !== "all website" ? 1 : 0) +
                        (dateRange.from && dateRange.to ? 1 : 0) +
                        (selectedCountry !== "all" ? 1 : 0)}
                    </span>
                  )}
                </button>
                <button
                  onClick={toggleDetailedView}
                  className="p-1.5 rounded-md text-sm font-medium flex items-center bg-gray-100 text-gray-700 hover:bg-gray-200"
                  title="Detailed view"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                  Details
                </button>
              </div>
            </div>

            {/* Visitor List with Virtualization */}
            <div
              ref={parentRef}
              className="flex-1 overflow-y-auto"
              style={{
                height: `calc(100vh - 180px)`,
                position: "relative",
              }}
            >
              {/* Total size of all items */}
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const visitor = allLiveUser?.data?.[virtualRow.index]
                  if (!visitor) return null

                  return (
                    <div
                      key={visitor?._id}
                      className={`p-3 border-b border-gray-100 cursor-pointer visitor-item ${
                        virtualRow.index === activeVisitor ? "active" : ""
                      } ${visitor?.unreadMessage && !unreadMessage.includes(visitor?._id) > 0 ? "bg-blue-50" : ""}`}
                      onClick={() => getChatListOfUser({ userId: visitor?._id })}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          {/* Serial Number */}
                          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
                            #{virtualRow.index + 1}
                          </span>
                          <span className="text-sm font-semibold text-[#00204A] font-poppins tracking-tight">
                            {visitor?.name || visitor?.visitorId}
                          </span>
                          <img
                            src={countryFlags[visitor.country]?.flag || "/placeholder.svg" || "/placeholder.svg"}
                            alt={countryFlags[visitor.country]?.name || "Country flag"}
                            className="w-5 h-3.5 object-cover rounded-sm"
                            title={countryFlags[visitor.country]?.name}
                            // onError={(e) => {
                            //   e.target.onerror = null;
                            //   e.target.src = "/placeholder.svg";
                            // }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatMessageTime(visitor?.updatedAt, visitors[activeVisitor]?.createdAt)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-600">
                            {formatMessageTime(visitor?.updatedAt, visitors[activeVisitor]?.createdAt)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {visitor?.lastMessage?.content
                              ? truncateString(visitor?.lastMessage?.content, 6)
                              : "No Message"}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {!!visitor?.unreadMessage && !unreadMessage.includes(visitor?._id) && (
                            <span className="flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full">
                              {visitor?.unreadMessage}
                            </span>
                          )}
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full flex items-center ${
                              visitor.isOnline
                                ? "text-green-700 bg-green-50 border border-green-200"
                                : "text-gray-600 bg-gray-50 border border-gray-200"
                            }`}
                          >
                            <Circle
                              className={`w-2 h-2 mr-1 ${visitor.isOnline ? "text-green-500" : "text-gray-400"}`}
                            />
                            {visitor.isOnline ? "Online" : "Offline"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Right Panel - Chat Area */}
          {openChatList && (
            <div className={`${showChat || !isMobile ? "flex" : "hidden"} md:flex flex-1 flex-col bg-gray-50 min-h-0`}>
              {/* Active Visitor Info */}
              <div className="shrink-0 px-3 py-2 bg-white border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  {isMobile && (
                    <button
                      className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600"
                      onClick={() => setShowChat(false)}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  )}
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 rounded-full bg-[#00204A] text-white">
                      <User className="w-4 h-4" />
                    </div>
                    {openChatList?.client && (
                      <div>
                        <div className="flex items-center space-x-2">
                          <h2 className="text-sm font-bold text-[#00204A] font-poppins tracking-tight">
                            {openChatList?.client?.visitorId}
                          </h2>
                          {openChatList?.client?.country && (
                            <img
                              src={countryFlags[openChatList?.client?.country]?.flag || "/placeholder.svg"}
                              alt={countryFlags[openChatList?.client?.country]?.name || "Country flag"}
                              className="w-5 h-3.5 object-cover rounded-sm"
                              // onError={(e) => {
                              //   e.target.onerror = null;
                              //   e.target.src = "/placeholder.svg";
                              // }}
                            />
                          )}

                          {/* Platform badge */}
                          <div className="flex items-center px-2 py-0.5 bg-gray-100 rounded-full">
                            {renderPlatformIcon(openChatList?.client?.website)}
                            <span className="ml-1 text-xs font-medium text-gray-700">
                              <Tooltip title={openChatList?.client?.website}>
                                {truncateString(openChatList?.client?.website, 30)}
                              </Tooltip>
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <span>
                            Last active:{" "}
                            {formatMessageTime(openChatList?.client?.updatedAt, openChatList?.client?.createdAt)}
                          </span>
                          <span>•</span>
                          <span>{openChatList?.client?.name || "new client"} </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-1">
                  <div className="relative options-container">
                    {setopenLiveChat && (
                      <button
                        className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
                        onClick={() => setopenLiveChat(false)}
                      >
                        <Close className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
                      onClick={() => setShowOptions(!showOptions)}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {/* Options dropdown */}
                    {showOptions && (
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10">
                        <div className="py-1">
                          <button
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            onClick={openEditModal}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </button>
                          {/* <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            Transfer Chat
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            End Chat
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
                            Report Abuse
                          </button> */}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Chat Messages */}
              <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-2 min-h-0" onScroll={handleChatScroll}>
                <div className="space-y-2">
                  {openChatList?.chatList?.map((msg, index) => (
                    <div
                      key={index}
                      ref={(el) => (messageRefs.current[msg._id] = el)}
                      className={`flex ${msg.sender?.userType != "CLIENT" ? "justify-end" : "justify-start"} ${
                        highlightedMessage === msg._id ? "animate-pulse" : ""
                      }`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-lg px-4 py-2 rounded-lg transition-all ${
                          msg.sender?.userType != "CLIENT" ? "bg-[#001a3b] text-white" : "bg-[#f0f0f0] text-gray-700"
                        } ${
                          highlightedMessage === msg._id
                            ? msg.sender?.userType != "CLIENT"
                              ? "ring-2 ring-blue-300 bg-[#002255]"
                              : "ring-2 ring-blue-300 bg-[#e8e8e8]"
                            : ""
                        }`}
                      >
                        <div className="flex justify-between items-center mb-0.5">
                          <span
                            className={`sender-name text-xs ${
                              msg.sender?.userType != "CLIENT" ? "text-gray-100" : "text-[#00204A]"
                            } font-roboto`}
                          >
                            {msg.sender?.userType != "CLIENT"
                              ? msg.sender?.name + ` (${msg.sender?.userType?.toLowerCase()})`
                              : messages?.data?.visitorId || openChatList?.client?.visitorId}
                          </span>
                          <span className="time-stamp text-xs ml-2">
                            {formatMessageTime(msg?.timestamp, new Date())}
                          </span>
                        </div>

                        {/* Render replied message if this message is a reply */}
                        {msg.replyTo && renderRepliedMessage(msg.replyTo)}

                        {renderMessageContent(msg)}

                        {/* Reply button */}
                        <div className="flex justify-end mt-1">
                          <button
                            onClick={() => handleReply(msg)}
                            className={`flex items-center text-xs py-0.5 px-1.5 rounded hover:bg-opacity-80 ${
                              msg.sender?.userType != "CLIENT"
                                ? "bg-[#143255] text-gray-100"
                                : "bg-gray-200 text-gray-700"
                            }`}
                          >
                            <MessageSquareReply className="w-3 h-3 mr-1" />
                            Reply
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Auto-scroll button that appears when scrolled up */}
              {showScrollButton && (
                <button
                  onClick={scrollToBottom}
                  className="absolute bottom-20 right-4 z-10 p-2 bg-[#00204A] text-white rounded-full shadow-lg hover:bg-opacity-90 transition-all"
                  aria-label="Scroll to bottom"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
              )}

              {/* Chat Input */}
              <div className="shrink-0 bg-white border-t border-gray-200">
                {/* Reply indicator */}
                {replyingTo && (
                  <div className="px-3 py-1 bg-gray-50 border-b border-gray-200 flex justify-between items-start">
                    <div className="flex-1">
                      <div className="text-xs text-gray-500">
                        Replying to {replyingTo.sender?._id === user?._id ? "yourself" : "visitor"}
                      </div>
                      <div className="text-sm text-gray-700 truncate">
                        {truncateString(replyingTo.content || replyingTo.message, 50)}
                      </div>
                    </div>
                    <button onClick={cancelReply} className="p-1 rounded-full hover:bg-gray-200 text-gray-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Attachments display */}
                {attachments.length > 0 && (
                  <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-2">Attachments:</div>
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((url, index) => {
                        const fileName = url.split("/").pop()
                        const isImage = isImageUrl(url)

                        return (
                          <div key={index} className="relative group">
                            {isImage ? (
                              <div className="w-20 h-20 border border-gray-200 rounded-md overflow-hidden bg-white">
                                <img
                                  src={url || "/placeholder.svg"}
                                  alt={fileName}
                                  className="w-full h-full object-cover"
                                  // onError={(e) => {
                                  //   e.target.onerror = null;
                                  //   e.target.src = "/placeholder.svg";
                                  // }}
                                />
                              </div>
                            ) : (
                              <div className="flex items-center bg-white rounded-md p-1.5 border border-gray-200">
                                <FileText className="w-4 h-4 text-gray-500 mr-1.5" />
                                <span className="text-xs text-gray-700 mr-1.5 max-w-[150px] truncate">{fileName}</span>
                              </div>
                            )}
                            <button
                              onClick={() => removeAttachment(index)}
                              className="absolute -top-1 -right-1 p-0.5 rounded-full bg-red-500 text-white hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Add a loading indicator for file uploads */}
                {isUploading && (
                  <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00204A] mr-2"></div>
                      <span className="text-xs text-gray-600">Uploading document...</span>
                    </div>
                  </div>
                )}

                <div className="p-2">
                  <div className="flex items-center bg-gray-50 rounded-lg p-1">
                    {/* File attachment */}
                    <label className="p-1.5 rounded-full hover:bg-white text-gray-600 transition-colors cursor-pointer">
                      <Paperclip className="w-4 h-4" />
                      <input type="file" className="hidden" onChange={handleFileUpload} />
                    </label>

                    <input
                      type="text"
                      className="flex-1 px-3 py-2 bg-transparent focus:outline-none chat-input font-inter"
                      placeholder="Type your message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault()
                          handleSendMessageWithAttachments()
                        }
                      }}
                    />

                    {/* Emoji picker */}
                    <div className="relative emoji-picker-container">
                      <button
                        className="p-1.5 rounded-full hover:bg-white text-gray-600 transition-colors"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      >
                        <Smile className="w-4 h-4" />
                      </button>

                      {showEmojiPicker && (
                        <div className="absolute bottom-10 right-0 z-10">
                          <EmojiPicker
                            onEmojiClick={handleEmojiClick}
                            width={280}
                            height={350}
                            previewConfig={{ showPreview: false }}
                            searchDisabled
                          />
                        </div>
                      )}
                    </div>

                    <button
                      className="ml-1 px-4 py-2 bg-[#00204A] text-white rounded-md send-button hover:bg-opacity-90 transition-colors font-roboto flex items-center"
                      onClick={handleSendMessageWithAttachments}
                      disabled={!message.trim() && attachments.length === 0}
                    >
                      <Send className="w-4 h-4 mr-1" /> Send
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden animate-fadeIn">
            <div className="bg-[#00204A] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">Edit User Information</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div className="space-y-1">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditInputChange}
                    className={`w-full pl-10 pr-3 py-2 border ${formErrors.name ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-2 ${formErrors.name ? "focus:ring-red-500" : "focus:ring-[#00204A]"} focus:border-[#00204A]`}
                    placeholder="Enter name"
                  />
                </div>
                {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
              </div>

              <div className="space-y-1">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditInputChange}
                    className={`w-full pl-10 pr-3 py-2 border ${formErrors.email ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-2 ${formErrors.email ? "focus:ring-red-500" : "focus:ring-[#00204A]"} focus:border-[#00204A]`}
                    placeholder="Enter email address"
                  />
                </div>
                {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
              </div>

              <div className="space-y-1">
                <label htmlFor="number" className="block text-sm font-medium text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    id="number"
                    name="number"
                    value={editFormData.number}
                    onChange={handleEditInputChange}
                    className={`w-full pl-10 pr-3 py-2 border ${formErrors.number ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-2 ${formErrors.number ? "focus:ring-red-500" : "focus:ring-[#00204A]"} focus:border-[#00204A]`}
                    placeholder="Enter phone number"
                  />
                </div>
                {formErrors.number && <p className="text-red-500 text-xs mt-1">{formErrors.number}</p>}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00204A]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#00204A] text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00204A]"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden animate-fadeIn">
            <div className="bg-[#00204A] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">Filter Visitors</h3>
              <button onClick={toggleFilterModal} className="text-white hover:text-gray-200 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Website Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Website</label>
                <div className="relative">
                  <select
                    className="w-full py-2 px-3 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00204A] focus:border-[#00204A] appearance-none"
                    value={currentWebsite}
                    onChange={(e) => handleWebsiteChange(e.target.value)}
                  >
                    {websites.map((site, index) => (
                      <option key={index} value={site}>
                        {site === "all website" ? "All Websites" : site}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-2.5 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Country Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Country</label>
                <div className="relative">
                  <select
                    className="w-full py-2 px-3 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00204A] focus:border-[#00204A] appearance-none"
                    value={selectedCountry}
                    onChange={(e) => handleCountryChange(e.target.value)}
                  >
                    <option value="all">All Countries</option>
                    {Object.entries(countryFlags).map(([code, country]) => (
                      <option key={code} value={code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-2.5 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Date Range Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Date Range</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From</label>
                    <input
                      type="date"
                      className="w-full py-2 px-3 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00204A] focus:border-[#00204A]"
                      value={dateRange.from}
                      onChange={(e) => handleDateRangeChange("from", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To</label>
                    <input
                      type="date"
                      className="w-full py-2 px-3 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00204A] focus:border-[#00204A]"
                      value={dateRange.to}
                      onChange={(e) => handleDateRangeChange("to", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Active Filters Summary */}
              <div className="pt-2 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Active Filters:</h4>
                <div className="flex flex-wrap gap-2">
                  {currentWebsite !== "all website" && (
                    <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs flex items-center">
                      <span className="mr-1">Website:</span>
                      <span className="font-medium">{currentWebsite.replace(/^https?:\/\//, "")}</span>
                    </div>
                  )}
                  {selectedCountry !== "all" && (
                    <div className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center">
                      <span className="mr-1">Country:</span>
                      <span className="font-medium">{countryFlags[selectedCountry]?.name}</span>
                    </div>
                  )}
                  {dateRange.from && dateRange.to && (
                    <div className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs flex items-center">
                      <span className="mr-1">Date:</span>
                      <span className="font-medium">
                        {new Date(dateRange.from).toLocaleDateString()} - {new Date(dateRange.to).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {currentWebsite === "all website" &&
                    selectedCountry === "all" &&
                    (!dateRange.from || !dateRange.to) && (
                      <div className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">No filters applied</div>
                    )}
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <button
                  type="button"
                  onClick={resetAllFilters}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00204A]"
                >
                  Reset All
                </button>
                <button
                  type="button"
                  onClick={applyFilters}
                  className="px-4 py-2 bg-[#00204A] text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00204A]"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed User List Modal */}
      {showDetailedView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl mx-4 my-6 overflow-hidden animate-fadeIn">
            <div className="bg-[#00204A] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">Detailed Visitor Information</h3>
              <button onClick={toggleDetailedView} className="text-white hover:text-gray-200 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              {/* Search and filter controls */}
              <div className="flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                  {/* Search input in detailed view */}
                  <input
                    type="text"
                    className="w-full pr-3 py-2 pl-8 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00204A] focus:border-[#00204A]"
                    placeholder="Search in detailed view..."
                    value={detailedViewSearch}
                    onChange={handleDetailedSearch}
                  />
                  <Search className="absolute left-2.5 top-2 h-4 w-4 text-gray-400" />
                </div>
                <button
                  onClick={() => {
                    setDetailedViewSearch("")
                    if (typeof getAllLiveUser === "function") {
                      getAllLiveUser({
                        page: 1,
                        tempSearchQuery: "",
                        resetPageData: true,
                        firstTime: true,
                      })
                    }
                  }}
                  className="px-3 py-2 bg-[#00204A] text-white rounded-md hover:bg-opacity-90 text-sm flex items-center"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Search
                </button>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Visitor ID
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Country
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Device
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Page
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Referral
                      </th>
                      {/* <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Visits
                      </th> */}
                      {/* <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Time Spent
                      </th> */}
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Last Active
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Domain Name
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedVisitors.length > 0 ? (
                      paginatedVisitors.map((visitor) => {
                        // Extract page slug from the full URL
                        let pageSlug = "homepage"
                        try {
                          if (visitor.page && visitor.page !== "NA") {
                            const urlObj = new URL(visitor.page)
                            pageSlug = urlObj.pathname === "/" ? "homepage" : urlObj.pathname
                          } else if (visitor.website === "Android APP") {
                            pageSlug = "Android App"
                          } else if (visitor.website && visitor.website !== "NA") {
                            try {
                              const urlObj = new URL(visitor.website)
                              pageSlug = urlObj.pathname === "/" ? "homepage" : urlObj.pathname
                            } catch (e) {
                              pageSlug = visitor.website
                            }
                          }
                        } catch (error) {
                          pageSlug = visitor.page || visitor.website || "Unknown"
                        }

                        return (
                          <tr key={visitor._id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="text-sm font-medium text-gray-900">{visitor.visitorId}</div>
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                ${
                  visitor.status === "MISSED"
                    ? "bg-red-100 text-red-800"
                    : visitor.status === "INPROGRESS"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-green-100 text-green-800"
                } mr-2`}
                                >
                                  {visitor.status}
                                </span>
                                <span
                                  className={`w-2 h-2 rounded-full ${visitor.isOnline ? "bg-green-500" : "bg-gray-300"}`}
                                  title={visitor.isOnline ? "Online" : "Offline"}
                                ></span>
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center">
                                <img
                                  src={countryFlags[visitor.country]?.flag || "/placeholder.svg"}
                                  alt={visitor.country || "Country flag"}
                                  className="w-5 h-3.5 object-cover rounded-sm mr-2"
                                />
                                <div className="text-sm text-gray-900">{visitor.country || "Unknown"}</div>
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{visitor.deviceType}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div
                                className="text-sm text-gray-900 max-w-[200px] truncate"
                                title={visitor.page || visitor.website}
                              >
                                {pageSlug}
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {visitor.refralUrl === "direct" ? "Direct" : visitor.refralUrl || "Unknown"}
                              </div>
                            </td>
                            {/* <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{visitor.visits}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{Math.round(visitor.totalTimeSpent)}s</div>
                            </td> */} 
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {formatMessageTime(visitor.updatedAt, visitor.createdAt)}
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-900 max-w-[200px] truncate" title={visitor.website}>
                                {visitor.website?.replace("https://www.","")  || "Unknown"}
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="10" className="px-3 py-4 text-center text-sm text-gray-500">
                          No visitors found matching your search criteria
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "text-gray-700 bg-white hover:bg-gray-50"
                    }`}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "text-gray-700 bg-white hover:bg-gray-50"
                    }`}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {allLiveUser?.data?.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(currentPage * itemsPerPage, allLiveUser?.paginations?.total || 0)}
                      </span>{" "}
                      of <span className="font-medium">{allLiveUser?.paginations?.total || 0}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                          currentPage === 1 ? "text-gray-300 cursor-not-allowed" : "text-gray-500 hover:bg-gray-50"
                        }`}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <span className="sr-only">Previous</span>
                        <svg
                          className="h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>

                      {pageNumbers.map((pageNumber, index) =>
                        pageNumber === "..." ? (
                          <span
                            key={`ellipsis-${index}`}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            aria-current={currentPage === pageNumber ? "page" : undefined}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNumber
                                ? "z-10 bg-[#00204A] border-[#00204A] text-white"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {pageNumber}
                          </button>
                        ),
                      )}

                      <button
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                          currentPage === totalPages
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-gray-500 hover:bg-gray-50"
                        }`}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <span className="sr-only">Next</span>
                        <svg
                          className="h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  )
}

export default ChatArea
